import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { advanceAPI } from '../../../services';
import { 
  DollarSign, 
  Send, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  Calculator
} from 'lucide-react';

const RequestAdvance = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    type: 'advance',
    amount: '',
    reason: ''
  });

  // Check if user is permanent employee
  const isPermanent = user?.employeeType === 'permanent';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || formData.amount <= 0) {
      setErrorMessage('Please enter a valid amount');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (!formData.reason.trim()) {
      setErrorMessage('Please provide a reason for your request');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setLoading(true);
      await advanceAPI.requestAdvance({
        type: formData.type,
        amount: parseFloat(formData.amount),
        reason: formData.reason
      });
      
      setSuccessMessage('Request submitted successfully! You will be notified once it is reviewed.');
      setTimeout(() => {
        setSuccessMessage('');
        // Reset form
        setFormData({
          type: 'advance',
          amount: '',
          reason: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting request:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to submit request');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (!isPermanent) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              Not Eligible
            </h3>
            <p className="text-yellow-800">
              Only permanent employees can request salary advances or loans. 
              Your current employee type is: <strong>{user?.employeeType || 'Not Set'}</strong>
            </p>
            <p className="text-yellow-700 mt-2 text-sm">
              Please contact HR if you believe this is an error.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-7 h-7 text-green-600" />
          Request Advance/Loan
        </h1>
        <p className="text-gray-600 mt-1">
          Submit a request for salary advance or loan
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">Important Information:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Advance:</strong> Full amount will be deducted from your next salary</li>
              <li><strong>Loan:</strong> Amount will be deducted in monthly EMIs (decided during approval)</li>
              <li>You can have only one active request at a time</li>
              <li>Your request will be reviewed by authorized personnel</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Request Form */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Request Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'advance' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.type === 'advance'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <DollarSign className={`w-8 h-8 mx-auto mb-2 ${
                    formData.type === 'advance' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <h3 className="font-semibold text-gray-900">Advance</h3>
                  <p className="text-xs text-gray-600 mt-1">One-time deduction</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'loan' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.type === 'loan'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <Calculator className={`w-8 h-8 mx-auto mb-2 ${
                    formData.type === 'loan' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <h3 className="font-semibold text-gray-900">Loan</h3>
                  <p className="text-xs text-gray-600 mt-1">EMI-based repayment</p>
                </div>
              </button>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
              Amount (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter amount"
              min="1"
              step="1"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the amount you need
            </p>
          </div>

          {/* Reason */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Explain why you need this advance/loan"
              rows="4"
              maxLength="500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.reason.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setFormData({
                  type: 'advance',
                  amount: '',
                  reason: ''
                });
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestAdvance;
